import type { Dict } from "../types";

export const salonInfo: Dict = {
  en: {

    // Direct Booking & QR Code
    "SalonInfo.directBookingLink": "Direct Booking Link",
    "SalonInfo.directBookingDescription":
      "Share this link with your clients so they can book appointments directly with your salon. This link bypasses the salon selection page and takes clients straight to your booking system.",
    "SalonInfo.linkCopied": "Link copied!",
    "SalonInfo.linkCopiedDescription": "Direct booking link copied to clipboard",
    "SalonInfo.copyFailed": "Failed to copy",
    "SalonInfo.copyFailedDescription": "Please copy the link manually",
    "SalonInfo.howToUse": "💡 How to use this link:",
    "SalonInfo.useWebsite": "Add it to your website or social media profiles",
    "SalonInfo.useEmail": "Include it in email signatures or newsletters",
    "SalonInfo.useText": "Share it in text messages with clients",
    "SalonInfo.useQR": "Generate a QR code for in-salon displays (see below)",
    "SalonInfo.qrCode": "QR Code",
    "SalonInfo.qrCodeDescription":
      "Generate a QR code that clients can scan to book appointments instantly. Perfect for reception desks, salon windows, business cards, and marketing materials.",
    "SalonInfo.downloadQR": "Download QR Code",
    "SalonInfo.qrDownloaded": "QR Code Downloaded",
    "SalonInfo.qrDownloadedDescription": "Your QR code has been saved as a PNG image",
    "SalonInfo.downloadFailed": "Download Failed",
    "SalonInfo.downloadFailedDescription": "Unable to download QR code",
    "SalonInfo.usageTips": "📱 Usage tips:",
    "SalonInfo.tipTableTents": "Print on table tents or counter displays for walk-in clients",
    "SalonInfo.tipWindows": "Add to salon windows or storefront signage",
    "SalonInfo.tipCards": "Include on business cards and flyers",
    "SalonInfo.tipWaiting": "Display in waiting areas",
    "SalonInfo.tipSocial": "Share on social media posts and stories",

    // Salon Settings
    "SalonInfo.salonSettings": "Salon Settings",
    "SalonInfo.advanceReminderTiming": "Advance Reminder Timing",
    "SalonInfo.reminder24h": "24 hours before",
    "SalonInfo.reminder48h": "48 hours before",
    "SalonInfo.reminder72h": "72 hours before",
    "SalonInfo.shortReminderTiming": "Short Reminder Timing",
    "SalonInfo.reminder30min": "30 minutes before",
    "SalonInfo.reminder1h": "1 hour before",
    "SalonInfo.reminder2h": "2 hours before",
    "SalonInfo.reminder3h": "3 hours before",
    "SalonInfo.reminder4h": "4 hours before",
    "SalonInfo.shortReminderProOnly": "Available on Pro and Enterprise plans",
    "SalonInfo.reviewRequestsLabel": "Google review requests",
    "SalonInfo.cashAutomationLabel": "Cash register automations (cash)",
    "SalonInfo.cashAutomationDescription": "On a cash payment, automatically prints the receipt, which opens the printer-connected cash drawer.",
    "SalonInfo.paidReceiptPromptLabel": "Print/email prompt on paid invoice",
    "SalonInfo.paidReceiptPromptDescription": "When an invoice becomes paid, immediately offer to print or email the receipt.",
    "SalonInfo.tipFromDrawerLabel": "The tip is taken from the cash drawer",
    "SalonInfo.tipFromDrawerDescription": "When an invoice becomes paid with a tip, the drawer opens and the full tip is recorded as a cash withdrawal, whatever the payment method.",
    "SalonInfo.autoPrintOnPaidLabel": "Automatically print on every paid invoice",
    "SalonInfo.autoPrintOnPaidDescription": "The receipt prints every time an invoice becomes paid, even when no cash is involved.",
    "SalonInfo.autoPrintVariantLabel": "Copies printed automatically",
    "SalonInfo.autoPrintVariantDescription": "Applies to prints triggered by the automations above. The merchant copy repeats the full invoice and adds the per seller totals.",
    "SalonInfo.autoPrintVariantClient": "Client copy only",
    "SalonInfo.autoPrintVariantBusiness": "Merchant copy only",
    "SalonInfo.autoPrintVariantBoth": "Both copies",
    "SalonInfo.paymentMethodsLabel": "Accepted payment methods",
    "SalonInfo.paymentMethodsDescription": "Uncheck the methods you do not accept. They disappear from checkout, but your past sales stay intact in reports.",
    "SalonInfo.paymentMethodLocked": "Always on",
    "SalonInfo.paymentMethodLockedHint": "This method is tied to the cash drawer, gift cards or online payments.",
    "SalonInfo.paymentMethodLastActive": "Keep at least one method active.",
    "SalonInfo.paymentMethodAdd": "Add a method",
    "SalonInfo.paymentMethodNamePlaceholder": "E.g. PayPal",
    "SalonInfo.paymentMethodReserved": "This name is already used by a built in method.",
    "SalonInfo.paymentMethodAdded": "Method added",
    "SalonInfo.paymentMethodDuplicate": "You already have a payment method with this name.",
    "SalonInfo.paymentMethodNameRequired": "Enter a name for the method.",
    "SalonInfo.paymentMethodsLoadFailed": "Could not load the payment methods. Reload the page before changing anything: what you see is not what is stored.",
    "SalonInfo.smsConfirmationLabel": "Client appointment confirmation",
    "SalonInfo.smsConfirmationDescription": "When on, new online bookings stay pending until the client confirms by SMS or email. You keep a manual confirm and cancel button.",
    "SalonInfo.confirmationAdvanceTiming": "Ask for confirmation",
    "SalonInfo.confirmationAdvance24h": "24 hours before",
    "SalonInfo.confirmationAdvance48h": "48 hours before",
    "SalonInfo.confirmationAdvance72h": "72 hours before",
    "SalonInfo.confirmationShortTiming": "Last chance",
    "SalonInfo.confirmationShort2h": "2 hours before",
    "SalonInfo.confirmationShort4h": "4 hours before",
    "SalonInfo.confirmationShort6h": "6 hours before",
    "SalonInfo.confirmationShort12h": "12 hours before",
    "SalonInfo.confirmationShortHelp": "A client who was never reached, or who booked too late for the first message, gets asked here.",
    "SalonInfo.confirmationFollowUpLabel": "Send a reminder if the client has not replied",
    "SalonInfo.confirmationFollowUpHelp": "One extra message, at the last chance moment, on the channels already used.",
    "SalonInfo.toggleEnabled": "Enabled",
    "SalonInfo.toggleDisabled": "Disabled",
    "SalonInfo.staffBookingAlertsLabel": "Email alert for new bookings",
    "SalonInfo.staffBookingAlertsDescription": "Email your team the moment a client books online or through the voice assistant. Bookings you create yourself never trigger an alert.",
    "SalonInfo.staffBookingAlertsEnabled": "Enabled",
    "SalonInfo.staffBookingAlertsDisabled": "Disabled",
    "SalonInfo.staffBookingAlertsScopeAll": "Everyone on the team",
    "SalonInfo.staffBookingAlertsScopeSelected": "Specific people",
    "SalonInfo.staffBookingAlertsScopeHint": "Owners are alerted about every booking. Employees are only alerted about bookings that involve them.",
    "SalonInfo.staffBookingAlertsNoStaff": "No team member has an email address yet.",
    "SalonInfo.staffBookingAlertsRoleOwner": "Owner",
    "SalonInfo.staffBookingAlertsRoleEmployee": "Employee",
    "SalonInfo.salonSettingsUpdated": "Salon settings updated successfully.",
    "SalonInfo.recurringAppointments": "Series booked online by your clients",
    "SalonInfo.recurringAppointmentsHelp": "Lets a client book an appointment that repeats. Your team can still create series from the calendar.",
    "SalonInfo.recurringEnabled": "Clients can book recurring appointments",
    "SalonInfo.recurringDisabled": "Recurring appointments are disabled",
    "SalonInfo.maxRecurringDuration": "Maximum Recurrence Duration",
    "SalonInfo.staffRecurringDuration": "Maximum Staff Recurrence Duration",
    "SalonInfo.staffRecurringDurationDescription": "Maximum window for recurring series created from the dashboard by you and your team",
    "SalonInfo.maxBlockedDuration": "Maximum Duration for Blocked Slots & Exceptional Openings",
    "SalonInfo.maxDuration3m": "3 months",
    "SalonInfo.maxDuration6m": "6 months",
    "SalonInfo.maxDuration9m": "9 months",
    "SalonInfo.maxDuration12m": "1 year",
    "SalonInfo.maxDuration18m": "1.5 years",
    "SalonInfo.maxDuration24m": "2 years",
    "SalonInfo.maxDuration36m": "3 years",
    "SalonInfo.maxDuration60m": "5 years",
    "SalonInfo.autoApprove": "Auto-Approve Appointments",
    "SalonInfo.autoApproveLockedByClientConfirmation": "Unavailable while customers confirm their own appointments. Turn that setting off first.",
    "SalonInfo.autoApproveEnabled": "Pending appointments are automatically approved when near their scheduled time",
    "SalonInfo.autoApproveDisabled": "Auto-approval is disabled: all appointments require manual approval",
    "SalonInfo.autoMarkPresent": "Auto-mark past appointments as Present",
    "SalonInfo.autoMarkPresentEnabled": "Confirmed appointments are automatically marked Present once they're over",
    "SalonInfo.autoMarkPresentDisabled": "Past appointments keep no attendance status until you mark them manually",
    "SalonInfo.autoMarkPresentDescription": "Safety net for your client reliability stats: if no one marks a client Present after their appointment, the system does it for you. Turn it off to always mark attendance manually. You can still switch any appointment to No-show at any time.",
    "SalonInfo.autoHideCancelled": "Remove cancelled appointments in one click",
    "SalonInfo.autoHideCancelledEnabled": "On: cancelling also removes it from the calendar",
    "SalonInfo.autoHideCancelledDisabled": "Off: cancelled appointments stay in red until you remove them",
    "SalonInfo.autoHideCancelledDescription": "When on, cancelling an appointment removes it from the calendar in one step instead of two. Nothing is deleted: it stays in the cancellation log, reports and client history, and you can show it again from the cancellation log.",
    "SalonInfo.calendarChangeConfirmation": "Confirm calendar changes",
    "SalonInfo.calendarChangeConfirmationEnabled": "A window asks to confirm each change",
    "SalonInfo.calendarChangeConfirmationDisabled": "Changes apply right away",
    "SalonInfo.calendarChangeConfirmationDescription": "Applies when you move or stretch an appointment or a blocked time, on desktop and on mobile.",
    "SalonInfo.appointmentOverlap": "Allow overlapping appointments",
    "SalonInfo.overlapEnabled": "Owners and employees can book overlapping appointments",
    "SalonInfo.overlapDisabled": "Overlapping appointments are blocked",
    "SalonInfo.appointmentOverlapDescription": "Useful when an employee can serve two clients in parallel during a pause window",
    "SalonInfo.outOfHoursBooking": "Out-of-hours appointments",
    "SalonInfo.outOfHoursBookingDescription": "Lets your team place an appointment before or after an employee's hours, from the calendar only, within the calendar time range. Online booking is not affected.",
    "SalonInfo.outOfHoursEnabled": "Owners and employees can book outside an employee's hours, in the calendar",
    "SalonInfo.outOfHoursDisabled": "Appointments stay within each employee's hours",
    "SalonInfo.partialRecurringSeries": "Create recurring series despite unavailable dates",
    "SalonInfo.partialRecurringSeriesDescription": "Without this, a single unavailable date refuses the whole series and nothing is created. With it, dates taken by another appointment or by a blocked slot are left out, the others are booked, and a message lists what was skipped. Applies to series booked from the dashboard.",
    "SalonInfo.partialRecurringSeriesEnabled": "Unavailable dates are skipped, the rest of the series is created",
    "SalonInfo.partialRecurringSeriesDisabled": "A single unavailable date refuses the whole series",
    "SalonInfo.maxBookingWindow": "Maximum Booking Window",
    "SalonInfo.maxBookingWindowDescription": "How far in advance clients can book online",
    "SalonInfo.maxBookingUnlimited": "Unlimited",
    "SalonInfo.maxBooking1Week": "1 week",
    "SalonInfo.maxBooking2Weeks": "2 weeks",
    "SalonInfo.maxBooking1Month": "1 month",
    "SalonInfo.maxBooking2Months": "2 months",
    "SalonInfo.maxBooking3Months": "3 months",
    "SalonInfo.maxBooking6Months": "6 months",
    "SalonInfo.maxBooking1Year": "1 year",
    "SalonInfo.bookingSlotInterval": "Time Slot Interval",
    "SalonInfo.bookingSlotIntervalDescription": "Time increment between available booking slots shown to clients",
    "SalonInfo.payrollFrequency": "Pay frequency",
    "SalonInfo.payrollFrequencyDescription": "How often you pay your staff. Governs pay periods, overtime and fixed salaries.",
    "SalonInfo.payFreqWeekly": "Weekly",
    "SalonInfo.payFreqBiweekly": "Every 2 weeks",
    "SalonInfo.payFreqMonthly": "Monthly",
    "SalonInfo.payrollAnchor": "Next cycle starts on",
    "SalonInfo.payrollAnchorDescription": "The first day of your next pay cycle. It also sets the day your work week starts, which is what overtime is counted against.",
    "SalonInfo.payrollEnterpriseOnly": "Available on the Enterprise plan",
    "SalonInfo.payrollFrequencyLocked": "Locked: your first payroll has been finalized. Changing the frequency now would overlap existing pay periods.",
    "SalonInfo.slotInterval15": "Every 15 minutes",
    "SalonInfo.slotInterval30": "Every 30 minutes",
    "SalonInfo.slotInterval10": "Every 10 minutes",
    "SalonInfo.calendarSlotInterval": "Calendar interval",
    "SalonInfo.calendarSlotIntervalHelp": "Size of the calendar slots and step of the times offered to staff.",
    "SalonInfo.intervalSavedBoth": "Interval saved. Online booking follows too: every {minutes} minutes.",
    "SalonInfo.intervalAlignTitle": "Align online booking too?",
    "SalonInfo.intervalAlignBody": "Online booking is set to every {online} minutes. Do you also want to offer times every {next} minutes to your clients?",
    "SalonInfo.intervalAlignBoth": "Align both",
    "SalonInfo.intervalKeepOnline": "Keep {online} min online",
    "SalonInfo.intervalDivergeTitle": "Are you sure?",
    "SalonInfo.intervalDivergeBody": "Your clients will see times every {next} minutes, while your calendar is divided every {calendar} minutes. Some bookings will fall between two lines of the calendar.",
    "SalonInfo.postCreateSilence": "Silence period after booking",
    "SalonInfo.postCreateSilenceDescription": "Only a date or time change notifies the client. Employee, room, duration and service changes stay silent. When the time does change, the message isn't sent right away: edits made within this window are grouped into a single notice showing the final time (and if you move it back, nothing is sent). Cancellations always notify immediately.",
    "SalonInfo.postCreateSilenceEnabled": "Time changes are grouped before notifying",
    "SalonInfo.postCreateSilenceDisabled": "Time changes notify the client right away",
    "SalonInfo.postCreateSilenceDuration": "Window duration",
    "SalonInfo.postCreateSilence1min": "1 minute",
    "SalonInfo.postCreateSilence5min": "5 minutes",
    "SalonInfo.postCreateSilence15min": "15 minutes",
    "SalonInfo.postCreateSilence30min": "30 minutes",
    "SalonInfo.postCreateSilence60min": "1 hour",
    "SalonInfo.autoApproveSameDay": "Same-Day Booking Threshold",
    "SalonInfo.autoApproveAdvance": "Advance Booking Threshold",
    "SalonInfo.autoApproveImmediate": "Immediately (always)",
    "SalonInfo.autoApprove15min": "15 minutes before",
    "SalonInfo.autoApprove30min": "30 minutes before",
    "SalonInfo.autoApprove45min": "45 minutes before",
    "SalonInfo.autoApprove60min": "1 hour before",
    "SalonInfo.autoApprove12h": "12 hours before",
    "SalonInfo.autoApprove24h": "24 hours before",
    "SalonInfo.autoApprove48h": "48 hours before",
    "SalonInfo.onlineBooking": "Online Booking",
    "SalonInfo.onlineBookingEnabled": "Clients can book appointments online",
    "SalonInfo.onlineBookingDisabled": "Online booking is disabled",
    "SalonInfo.hidePricesOnline": "Hide prices",
    "SalonInfo.hidePricesOnlineDescription": "Prices are not shown to clients, neither on the booking page nor in their appointment emails. They remain on invoices.",
    "SalonInfo.hidePricesOnlineEnabled": "Prices are hidden during online booking",
    "SalonInfo.hidePricesOnlineDisabled": "Prices are shown during online booking",
    "SalonInfo.showNextAvailability": "Show next availability per employee",
    "SalonInfo.showNextAvailabilityDescription": "Clients see each employee's next available time slot on the employee-selection step, so they can pick whoever can take them soonest.",
    "SalonInfo.showNextAvailabilityEnabled": "Next availability is shown during online booking",
    "SalonInfo.showNextAvailabilityDisabled": "Next availability is hidden during online booking",
    "SalonInfo.technicalSheetsEditable": "Editable technical sheets",
    "SalonInfo.technicalSheetsEditableDescription": "Lets your team correct the text of a saved technical sheet. The date and the employee stay locked, and every correction leaves a visible note on the sheet. This setting applies to all your locations.",
    "SalonInfo.technicalSheetsEditableEnabled": "Editable",
    "SalonInfo.technicalSheetsEditableDisabled": "Locked",
    "SalonInfo.employeeOnlineBooking": "Per-Employee Booking",
    "SalonInfo.employeeOnlineBookingDescription": "Control which employees are available for online booking.",
    "SalonInfo.calendarColorMode": "Calendar Colors",
    "SalonInfo.colorByEmployee": "By Employee",
    "SalonInfo.colorByService": "By Service",
    "SalonInfo.colorByStatus": "By Status",
    "SalonInfo.greyOutPastAppointments": "Grey out completed appointments",
    "SalonInfo.greyOutPastAppointmentsEnabled": "Past appointments turn slate grey once they're over",
    "SalonInfo.greyOutPastAppointmentsDisabled": "Past appointments keep their normal color",
    "SalonInfo.greyOutPastAppointmentsDescription": "Visual cue on the calendar: once an appointment's end time has passed, it turns a muted slate grey so it's easy to tell at a glance what's done versus still coming. Cancelled (red) and no-show (purple) appointments keep their color.",
    "SalonInfo.darkMode": "Dark mode",
    "SalonInfo.darkModeEnabled": "Dark colors",
    "SalonInfo.darkModeDisabled": "Light colors",
    "SalonInfo.darkModeDescription": "Applies to this device only, and stays off until you turn it on. Everyone else keeps their own choice, and nothing changes for your clients.",
    "SalonInfo.calendarDisplayHours": "Calendar Time Range",
    "SalonInfo.calendarDisplayHoursHelp": "Visible hours on the calendar grid (24h format). Default 8 to 24.",
    "SalonInfo.hideNonWorking": "Hide non-working employees (Day view)",
    "SalonInfo.hideNonWorkingDescription": "In Day view, only show columns for employees scheduled that day (plus anyone with an appointment or a personal block).",
    "SalonInfo.hideNonWorkingNeedsIndividual": "Available only when individual schedules are enabled.",
    "SalonInfo.columnDensity": "Employee column width",
    "SalonInfo.collapsiblePickers": "Collapsed lists",
    "SalonInfo.collapsiblePickersHelp": "Service and product lists in appointments and invoices open collapsed by category.",
    "SalonInfo.collapsiblePickersEnabled": "On: when booking an appointment and on invoices, services and products are sorted into collapsed groups. Click a group to show its content.",
    "SalonInfo.collapsiblePickersDisabled": "Off: service and product lists show everything at once.",
    "SalonInfo.allowNegativeStock": "Allow negative stock",
    "SalonInfo.allowNegativeStockDesc": "Lets you sell a product whose stock is at zero. The register asks for confirmation, and the stock goes below zero until the next restock. Transfers and inventory adjustments stay blocked below zero.",
    "SalonInfo.greencircleFeeLabel": "Charge a Green Circle fee",
    "SalonInfo.greencircleFeeDesc": "A \"Green Circle fee\" field appears on every invoice. You enter the amount to charge.",
    "SalonInfo.greencircleFeeTaxableLabel": "This fee is taxable",
    "SalonInfo.greencircleFeeTaxableDesc": "When taxable, the fee is added before taxes. Otherwise it is added after taxes, like a tip.",
    "SalonInfo.greencircleFeeSaveError": "The setting could not be saved.",
    "SalonInfo.columnDensityDescription": "Width of the employee columns in Day view on desktop. Appointment card text stays the same at every setting.",
    "SalonInfo.columnDensity.wide": "Wide",
    "SalonInfo.columnDensity.medium": "Medium",
    "SalonInfo.columnDensity.narrow": "Narrow",
    "SalonInfo.columnDensityHint.wide": "The original layout, and the most readable. About 8 columns on a typical screen.",
    "SalonInfo.columnDensityHint.medium": "Narrower columns, with nothing changed in the headers or on the cards. About 12 to 15 columns.",
    "SalonInfo.columnDensityHint.narrow": "The most columns. Only the header gives ground: smaller avatar, shorter name, abbreviated hours. Appointment cards stay identical.",
    "SalonInfo.hideNonWorkingEnabled": "Non-working employees are hidden",
    "SalonInfo.hideNonWorkingDisabled": "All employees are shown",
    "SalonInfo.calendarDisplayStart": "Start hour",
    "SalonInfo.calendarDisplayEnd": "End hour",
    "SalonInfo.displayHoursInvalid": "Start hour must be lower than end hour (0 to 23 / 1 to 24).",
    "SalonInfo.salonLogo": "Salon Logo",
    "SalonInfo.salonLogoDescription": "Upload your salon logo. It will be displayed on your online booking page.",
    "SalonInfo.removeLogo": "Remove the logo",
    "SalonInfo.uploadLogo": "Upload Logo",
    "SalonInfo.uploadingLogo": "Uploading...",
    "SalonInfo.logoUpdated": "Salon logo updated successfully.",
    "SalonInfo.logoRemoved": "Salon logo removed.",
    "SalonInfo.invalidLogoFile": "Please select an image file.",
    "SalonInfo.logoFileTooLarge": "Logo file must be under 5MB.",
    "SalonInfo.salonPolicies": "Salon Policies",
    "SalonInfo.salonPoliciesDescription": "Define your salon policies that will be visible to your clients when booking.",
    "SalonInfo.sectionCancellation": "Cancellation",
    "SalonInfo.cancellationPolicy": "Cancellation Policy",
    "SalonInfo.cancellationPolicyPlaceholder": "Describe your cancellation policy (e.g., cancellations must be made at least 24 hours in advance...)",
    "SalonInfo.cancelCutoff.toggleLabel": "Block cancellation and rescheduling on the client side",
    "SalonInfo.cancelCutoff.toggleEnabled": "Enabled",
    "SalonInfo.cancelCutoff.toggleDisabled": "Disabled",
    "SalonInfo.cancelCutoff.delayLabel": "Lead time before the appointment",
    "SalonInfo.cancelCutoff.option6": "6 hours",
    "SalonInfo.cancelCutoff.option12": "12 hours",
    "SalonInfo.cancelCutoff.option18": "18 hours",
    "SalonInfo.cancelCutoff.option24": "24 hours",
    "SalonInfo.cancelCutoff.option48": "48 hours",
    "SalonInfo.cancelCutoff.option72": "72 hours",
    "SalonInfo.cancelCutoff.helpText": "Your clients will not be able to cancel or reschedule their appointment via the app less than {hours} h before the scheduled time. You can still do it for them from the dashboard.",
    "SalonInfo.sectionLateness": "Lateness",
    "SalonInfo.tabProfile": "Profile",
    "SalonInfo.tabIdentity": "Identity",
    "SalonInfo.tabPolicies": "Policies",
    "SalonInfo.tabGeneral": "General settings",
    "SalonInfo.tabPayments": "Payments",
    "SalonInfo.sectionOnlinePayments": "Online payments",
    "SalonInfo.tabLocations": "Locations",
    "SalonInfo.headerSubtitle": "Manage your salon's information, policies and settings.",

    // General settings tab: sections (2026-10-13)
    "SalonInfo.general.share.title": "Share your booking page",
    "SalonInfo.general.share.intro": "The link and QR code that take your clients straight to your page.",
    "SalonInfo.general.online.title": "Online booking",
    "SalonInfo.general.online.intro": "What your clients see and can do when they book on their own. Nothing here affects your team's calendar.",
    "SalonInfo.general.online.offNote": "Online booking is turned off: the settings below are kept, and will apply again as soon as you turn it back on.",
    "SalonInfo.general.calendar.title": "Calendar",
    "SalonInfo.general.calendar.intro": "What your team sees in the calendar, and how it reacts when they book, move or cancel an appointment.",
    "SalonInfo.general.calendar.grid": "Grid",
    "SalonInfo.general.calendar.display": "Calendar display",
    "SalonInfo.general.calendar.teamActions": "Team actions",
    "SalonInfo.general.notifications.title": "Notifications",
    "SalonInfo.general.notifications.intro": "The emails and text messages sent to your clients, and the alerts sent to your team.",
    "SalonInfo.general.notifications.reminders": "Client reminders",
    "SalonInfo.general.notifications.confirmation": "Confirmation and approval",
    "SalonInfo.general.notifications.afterChange": "After a change",
    "SalonInfo.general.notifications.afterAppointment": "After the appointment",
    "SalonInfo.general.notifications.teamAlerts": "Alerts to your team",
    "SalonInfo.general.notifications.reviewRequestsHelp": "Requires the Google link, entered in the Identity tab.",
    "SalonInfo.general.cash.title": "Checkout and invoices",
    "SalonInfo.general.cash.intro": "What happens at checkout when an invoice is paid.",
    "SalonInfo.general.cash.paymentMethods": "Payment methods",
    "SalonInfo.general.cash.drawer": "Drawer and printing",
    "SalonInfo.general.cash.invoice": "Invoice",
    "SalonInfo.general.clients.title": "Clients",
    "SalonInfo.general.clients.intro": "What your team can correct in your clients' files.",
    "SalonInfo.general.region.title": "Region and language",
    "SalonInfo.general.region.intro": "The taxes, the time and the language of the messages sent to your clients.",
    "SalonInfo.general.payroll.title": "Payroll",
    "SalonInfo.general.payroll.intro": "Your team's pay schedule. These settings do not affect booking.",
    "SalonInfo.general.payroll.ownerOnly": "Only the owner can change payroll settings.",
    "SalonInfo.general.device.title": "On this device",
    "SalonInfo.general.device.intro": "This setting only applies to this computer or phone, not to your team.",
    "SalonInfo.tileOnlineBooking": "Online booking",
    "SalonInfo.tileBookingWindow": "Booking window",
    "SalonInfo.tileTimezone": "Timezone",
    "SalonInfo.tileLanguage": "Language",
    "SalonInfo.tileEnabled": "On",
    "SalonInfo.tileDisabled": "Off",
    "SalonInfo.tileUnlimited": "Unlimited",
    "SalonInfo.tileDaysSuffix": "days",
    "SalonInfo.subAvailability": "Availability",
    "SalonInfo.subRecurrence": "Recurrence",
    "SalonInfo.subAutoApprove": "Auto-Approval",
    "SalonInfo.subCalendarDisplay": "Calendar Display",
    "SalonInfo.subRemindersBlocks": "Reminders & Blocks",
    "SalonInfo.locationsDivider": "Multiple Locations",
    "SalonInfo.identityCard": "Salon Identity",
    "SalonInfo.identityCardDescription": "Name, logo and contact information shown to clients",
    "SalonInfo.taxNumbers": "Tax Numbers",
    "SalonInfo.taxNumbersDescription": "Displayed on your invoices (Quebec)",
    "SalonInfo.linkAndQR": "Direct Link & QR Code",
    "SalonInfo.linkAndQRDescription": "Share this link with your clients",
    "SalonInfo.activationCard": "Online Booking Activation",
    "SalonInfo.activationCardDescription": "Enable the service and choose available employees",
    "SalonInfo.bookingRules": "Booking Rules",
    "SalonInfo.bookingRulesDescription": "Availability, intervals, recurrence, approval",
    "SalonInfo.colorByEmployeeShort": "By employee",
    "SalonInfo.colorByServiceShort": "By service",
    "SalonInfo.colorByRoomShort": "By room",
    "SalonInfo.colorByRoomDescription": "Main salon, room B...",
    "SalonInfo.colorModeHelp": "How appointments are colored in the calendar view",
    "SalonInfo.latePolicy": "Late Policy",
    "SalonInfo.latePolicyPlaceholder": "Describe your late policy (e.g., a delay of more than 15 minutes without notice will result in automatic cancellation...)",
    "SalonInfo.savePolicies": "Save Policies",
    "SalonInfo.policiesUpdated": "Salon policies updated successfully.",
    "SalonInfo.viewPolicies": "View policies",
    "SalonInfo.website": "Website",
    "SalonInfo.policiesDialogTitle": "Salon Policies",
    "SalonInfo.understood": "Understood",
    "SalonInfo.timezoneLabel": "Salon time zone",
    "SalonInfo.defaultLanguageLabel": "Default language",
    "SalonInfo.provinceLabel": "Province",
    "SalonInfo.province.confirmTitle": "Change province?",
    "SalonInfo.province.confirmBody": "This updates your billing taxes and can change your timezone. Already-issued invoices are not affected.",
    "SalonInfo.province.syncTimezone": "Also set my timezone to {tz}",
    "SalonInfo.province.taxesPreview": "Taxes: {lines}",
    "SalonInfo.province.nudge": "Confirm your salon's province so your taxes and timezone are correct.",
    "SalonInfo.province.nudgeCta": "Set province",
    "SalonInfo.tz.pacific": "Pacific Time (PST, UTC-8)",
    "SalonInfo.tz.mountain": "Mountain Time (MST, UTC-7)",
    "SalonInfo.tz.central": "Central Time (CST, UTC-6)",
    "SalonInfo.tz.eastern": "Eastern Time (EST, UTC-5)",
    "SalonInfo.tz.atlantic": "Atlantic Time (AST, UTC-4)",
    "SalonInfo.tz.newfoundland": "Newfoundland Time (NST, UTC-3:30)",

    // Location Manager
    "LocationManager.title": "Multi-Location Management",
    "LocationManager.description": "Manage your salon locations and settings",
    "LocationManager.addLocation": "Add Location",
    "LocationManager.manageFromDefault": "Locations are managed from the default location.",
    "LocationManager.activeLocations": "Active Locations",
    "LocationManager.archived": "Archived",
    "LocationManager.onlineBookingAlways": "Online Booking (Always On)",
    "LocationManager.publicListingAlways": "Public Listing (Always On)",
    "LocationManager.onlineBooking": "Online Booking",
    "LocationManager.publicListing": "Public Listing",
    "LocationManager.noLocations": "No additional locations yet",
    "LocationManager.clickToAdd": 'Click "Add Location" to get started',
    "LocationManager.restore": "Restore",
    "LocationManager.deleteForever": "Delete Forever",
    "LocationManager.noArchived": "No archived locations",
    "LocationManager.addNewLocation": "Add New Location",
    "LocationManager.addLocationDescription": "Enter the details for your new salon location.",
    "LocationManager.locationName": "Location Name *",
    "LocationManager.locationNamePlaceholder": "Downtown Branch",
    "LocationManager.address": "Address",
    "LocationManager.addressPlaceholder": "123 Main St, City, State",
    "LocationManager.phone": "Phone",
    "LocationManager.phonePlaceholder": "+1 (555) 000-0000",
    "LocationManager.email": "Email",
    "LocationManager.emailPlaceholder": "location@salon.com",
    "LocationManager.cancel": "Cancel",
    "LocationManager.archiveTitle": "Archive Location?",
    "LocationManager.archiveDescription":
      "This will archive the location. You can restore it later from the Archived tab.",
    "LocationManager.archive": "Archive",
    "LocationManager.permanentDeleteTitle": "Permanently Delete?",
    "LocationManager.permanentDeleteDescription":
      "This action cannot be undone. All data associated with this location will be permanently deleted.",
    "LocationManager.permanentDelete": "Delete Permanently",
    "LocationManager.success": "Success",
    "LocationManager.error": "Error",
    "LocationManager.locationAdded": "Location added successfully",
    "LocationManager.locationArchived": "Location archived successfully",
    "LocationManager.locationRestored": "Location restored successfully",
    "LocationManager.locationDeleted": "Location permanently deleted",
    "LocationManager.nameRequired": "Location name is required",
    "LocationManager.loading": "Loading locations...",
    "LocationManager.calendarColorMode": "Calendar Colors",
    "LocationManager.colorByEmployee": "By Employee",
    "LocationManager.colorByService": "By Service",
    "LocationManager.limitReached": "Limit reached. Contact us to add more locations.",
    "LocationManager.upgradeForMultiLocation": "Upgrade to Enterprise plan to access multi-location management",
    "LocationManager.employees": "Employees",
    "LocationManager.services": "Services",
    "LocationManager.apptsThisMonth": "Appts this month",
    "LocationManager.revenueThisMonth": "Revenue this month",
    "LocationManager.online": "Online",
    "LocationManager.offline": "Offline",
    "LocationManager.defaultIndicator": "Default location",
    "LocationManager.actionsMenu": "Location actions",

    // Buffer Time
    "BufferTime.title": "Buffer Time Between Appointments",
    "BufferTime.description":
      "This time is automatically added after each appointment for cleanup or preparation. Applies only to client bookings.",
    "BufferTime.noBuffer": "None",
    "BufferTime.updated": "Buffer time updated",
    "BufferTime.noHairdressers": "Add employees first to configure buffer times.",

    // Any-employee assignment rule
    "Owner.salonInfo.anyAssignment.title": "Assignment without a preference",
    "Owner.salonInfo.anyAssignment.help":
      "When a client books online without picking a professional, this is how the appointment is assigned.",
    "Owner.salonInfo.anyAssignment.random": "At random",
    "Owner.salonInfo.anyAssignment.randomHelp":
      "Drawn among the professionals free at that time. This is the current setting.",
    "Owner.salonInfo.anyAssignment.leastLoaded": "The least busy that day",
    "Owner.salonInfo.anyAssignment.leastLoadedHelp":
      "The one with the fewest minutes already booked that day.",
    "Owner.salonInfo.anyAssignment.roundRobin": "Each in turn",
    "Owner.salonInfo.anyAssignment.roundRobinHelp":
      "The one who has waited longest since their last assignment without a preference.",
    "Owner.salonInfo.anyAssignment.priorityOrder": "Priority order",
    "Owner.salonInfo.anyAssignment.priorityOrderHelp":
      "The first professional free in the order you set below.",
    "Owner.salonInfo.anyAssignment.priorityListHelp":
      "Drag the professionals to change their order. A new person joins at the end of the list.",
    "Owner.salonInfo.anyLocation.title": "Booking at any location",
    "Owner.salonInfo.anyLocation.help":
      "Your clients can choose Any location: the system assigns them the first free location, in the order below.",
    "Owner.salonInfo.anyLocation.brandWide": "This setting applies to all your locations.",
    "Owner.salonInfo.anyLocation.toggle": "Offer Any location",
    "Owner.salonInfo.anyLocation.toggleHelp":
      "Clients will see this option only if at least one service is offered at two locations.",
    "Owner.salonInfo.anyLocation.priorityTitle": "Location priority order",
    "Owner.salonInfo.anyLocation.priorityHelp":
      "Drag the locations to change their order. When several are free at the same time, the first one in this order is chosen.",
    "Owner.salonInfo.anyLocation.bookingOff": "Online booking turned off",
    "Owner.salonInfo.anyLocation.tableTitle": "Equivalent services",
    "Owner.salonInfo.anyLocation.tableHelp":
      "Indicate, row by row, which service at each location matches the same service. Only linked services are offered in Any location mode.",
    "Owner.salonInfo.anyLocation.notOffered": "Not offered here",
    "Owner.salonInfo.anyLocation.hiddenOnline": "(hidden online)",
    "Owner.salonInfo.anyLocation.notBookableOnline": "(not bookable online)",
    "Owner.salonInfo.anyLocation.minutes": "min",
    "Owner.salonInfo.anyLocation.suggested": "Suggested",
    "Owner.salonInfo.anyLocation.singleServiceNote": "A single service is not offered to clients.",
    "Owner.salonInfo.anyLocation.addRow": "Add a row",
    "Owner.salonInfo.anyLocation.removeRow": "Remove",
    "Owner.salonInfo.anyLocation.suggest": "Suggest matches",
    "Owner.salonInfo.anyLocation.noSuggestion": "No obvious match found.",
    "Owner.salonInfo.anyLocation.save": "Save",
    "Owner.salonInfo.anyLocation.saving": "Saving…",
    "Owner.salonInfo.anyLocation.loadError": "Could not load this setting.",
    "Owner.salonInfo.anyLocation.retry": "Try again",
    "Owner.salonInfo.anyLocation.dragHandle": "Move",
    "Owner.salonInfo.anyLocation.errors.notRootOwner": "Only the main owner can change this setting.",
    "Owner.salonInfo.anyLocation.errors.salonNotFound": "Salon not found.",
    "Owner.salonInfo.anyLocation.errors.groupEmpty": "Choose at least one service on this row.",
    "Owner.salonInfo.anyLocation.errors.serviceOutsideBrand":
      "This service no longer belongs to your active locations.",
    "Owner.salonInfo.anyLocation.errors.duplicateLocation": "A row can only contain one service per location.",
    "Owner.salonInfo.anyLocation.errors.serviceInTwoGroups": "This service is already used on another row.",
    "Owner.salonInfo.anyLocation.errors.malformed": "The matches could not be read. Reload the page.",
    "Owner.salonInfo.anyLocation.errors.foreignLocation": "A location in the list does not belong to your brand.",
  },
  fr: {

    // Direct Booking & QR Code
    "SalonInfo.directBookingLink": "Lien de réservation directe",
    "SalonInfo.directBookingDescription":
      "Partagez ce lien avec vos clients pour qu'ils puissent réserver des rendez-vous directement avec votre salon. Ce lien contourne la page de sélection du salon et dirige les clients directement vers votre système de réservation.",
    "SalonInfo.linkCopied": "Lien copié !",
    "SalonInfo.linkCopiedDescription": "Lien de réservation directe copié dans le presse-papier",
    "SalonInfo.copyFailed": "Échec de la copie",
    "SalonInfo.copyFailedDescription": "Veuillez copier le lien manuellement",
    "SalonInfo.howToUse": "💡 Comment utiliser ce lien :",
    "SalonInfo.useWebsite": "Ajoutez-le à votre site web ou vos profils de réseaux sociaux",
    "SalonInfo.useEmail": "Incluez-le dans les signatures d'e-mail ou les infolettres",
    "SalonInfo.useText": "Partagez-le dans les messages texte avec vos clients",
    "SalonInfo.useQR": "Générez un code QR pour les affichages en salon (voir ci-dessous)",
    "SalonInfo.qrCode": "Code QR",
    "SalonInfo.qrCodeDescription":
      "Générez un code QR que les clients peuvent scanner pour réserver des rendez-vous instantanément. Parfait pour les comptoirs de réception, les vitrines de salon, les cartes de visite et le matériel marketing.",
    "SalonInfo.downloadQR": "Télécharger le code QR",
    "SalonInfo.qrDownloaded": "Code QR téléchargé",
    "SalonInfo.qrDownloadedDescription": "Votre code QR a été enregistré en tant qu'image PNG",
    "SalonInfo.downloadFailed": "Échec du téléchargement",
    "SalonInfo.downloadFailedDescription": "Impossible de télécharger le code QR",
    "SalonInfo.usageTips": "📱 Conseils d'utilisation :",
    "SalonInfo.tipTableTents": "Imprimez sur des présentoirs de table ou de comptoir pour les clients sans rendez-vous",
    "SalonInfo.tipWindows": "Ajoutez aux vitrines du salon ou à l'enseigne de la devanture",
    "SalonInfo.tipCards": "Incluez sur les cartes de visite et les dépliants",
    "SalonInfo.tipWaiting": "Affichez dans les zones d'attente",
    "SalonInfo.tipSocial": "Partagez sur les publications et stories de réseaux sociaux",

    // Salon Settings
    "SalonInfo.salonSettings": "Paramètres du salon",
    "SalonInfo.advanceReminderTiming": "Délai du rappel à l'avance",
    "SalonInfo.reminder24h": "24 heures avant",
    "SalonInfo.reminder48h": "48 heures avant",
    "SalonInfo.reminder72h": "72 heures avant",
    "SalonInfo.shortReminderTiming": "Délai du rappel court",
    "SalonInfo.reminder30min": "30 minutes avant",
    "SalonInfo.reminder1h": "1 heure avant",
    "SalonInfo.reminder2h": "2 heures avant",
    "SalonInfo.reminder3h": "3 heures avant",
    "SalonInfo.reminder4h": "4 heures avant",
    "SalonInfo.shortReminderProOnly": "Disponible avec les forfaits Pro et Enterprise",
    "SalonInfo.reviewRequestsLabel": "Demandes d'avis Google",
    "SalonInfo.cashAutomationLabel": "Automatisations de caisse (comptant)",
    "SalonInfo.cashAutomationDescription": "À un paiement comptant, imprime automatiquement le reçu, ce qui ouvre le tiroir-caisse relié à l'imprimante.",
    "SalonInfo.paidReceiptPromptLabel": "Invite d'impression/envoi à la facture payée",
    "SalonInfo.paidReceiptPromptDescription": "Au passage à « payé », propose directement d'imprimer ou d'envoyer le reçu par courriel.",
    "SalonInfo.tipFromDrawerLabel": "Le pourboire est prélevé du tiroir-caisse",
    "SalonInfo.tipFromDrawerDescription": "Au passage à « payé » avec un pourboire, le tiroir s'ouvre et le pourboire complet est enregistré comme sortie de caisse, quel que soit le mode de paiement.",
    "SalonInfo.autoPrintOnPaidLabel": "Imprimer automatiquement à chaque facture payée",
    "SalonInfo.autoPrintOnPaidDescription": "Le reçu s'imprime à chaque passage à « payé », même sans paiement comptant.",
    "SalonInfo.autoPrintVariantLabel": "Copies imprimées automatiquement",
    "SalonInfo.autoPrintVariantDescription": "S'applique aux impressions déclenchées par les automatisations ci-dessus. La copie commerçant reprend la facture complète et y ajoute les totaux par vendeur.",
    "SalonInfo.autoPrintVariantClient": "Copie client seulement",
    "SalonInfo.autoPrintVariantBusiness": "Copie commerçant seulement",
    "SalonInfo.autoPrintVariantBoth": "Les deux copies",
    "SalonInfo.paymentMethodsLabel": "Méthodes de paiement acceptées",
    "SalonInfo.paymentMethodsDescription": "Décochez les méthodes que vous n'acceptez pas. Elles disparaissent de la caisse, mais vos ventes passées restent intactes dans les rapports.",
    "SalonInfo.paymentMethodLocked": "Toujours actif",
    "SalonInfo.paymentMethodLockedHint": "Cette méthode est liée au tiroir-caisse, aux cartes cadeaux ou aux paiements en ligne.",
    "SalonInfo.paymentMethodLastActive": "Gardez au moins une méthode active.",
    "SalonInfo.paymentMethodAdd": "Ajouter une méthode",
    "SalonInfo.paymentMethodNamePlaceholder": "Ex. : PayPal",
    "SalonInfo.paymentMethodReserved": "Ce nom est déjà utilisé par une méthode du système.",
    "SalonInfo.paymentMethodAdded": "Méthode ajoutée",
    "SalonInfo.paymentMethodDuplicate": "Vous avez déjà une méthode de paiement portant ce nom.",
    "SalonInfo.paymentMethodNameRequired": "Entrez un nom pour la méthode.",
    "SalonInfo.paymentMethodsLoadFailed": "Impossible de charger les méthodes de paiement. Rechargez la page avant de modifier quoi que ce soit : ce qui est affiché n'est pas ce qui est enregistré.",
    "SalonInfo.smsConfirmationLabel": "Confirmation du rendez-vous par le client",
    "SalonInfo.smsConfirmationDescription": "Quand c'est activé, une nouvelle réservation en ligne reste en attente jusqu'à ce que le client confirme par SMS ou courriel. Vous gardez un bouton manuel pour confirmer ou annuler.",
    "SalonInfo.confirmationAdvanceTiming": "Demander la confirmation",
    "SalonInfo.confirmationAdvance24h": "24 heures avant",
    "SalonInfo.confirmationAdvance48h": "48 heures avant",
    "SalonInfo.confirmationAdvance72h": "72 heures avant",
    "SalonInfo.confirmationShortTiming": "Dernière chance",
    "SalonInfo.confirmationShort2h": "2 heures avant",
    "SalonInfo.confirmationShort4h": "4 heures avant",
    "SalonInfo.confirmationShort6h": "6 heures avant",
    "SalonInfo.confirmationShort12h": "12 heures avant",
    "SalonInfo.confirmationShortHelp": "Une cliente qu'on n'a jamais jointe, ou qui a réservé trop tard pour le premier message, est sollicitée ici.",
    "SalonInfo.confirmationFollowUpLabel": "Relancer si la cliente n'a pas répondu",
    "SalonInfo.confirmationFollowUpHelp": "Un message de plus, au moment de la dernière chance, sur les canaux déjà utilisés.",
    "SalonInfo.toggleEnabled": "Activé",
    "SalonInfo.toggleDisabled": "Désactivé",
    "SalonInfo.staffBookingAlertsLabel": "Alerte courriel des nouvelles réservations",
    "SalonInfo.staffBookingAlertsDescription": "Prévenez votre équipe par courriel dès qu'un client réserve en ligne ou par l'assistant vocal. Les rendez-vous que vous créez vous-même ne déclenchent aucune alerte.",
    "SalonInfo.staffBookingAlertsEnabled": "Activée",
    "SalonInfo.staffBookingAlertsDisabled": "Désactivée",
    "SalonInfo.staffBookingAlertsScopeAll": "Toute l'équipe",
    "SalonInfo.staffBookingAlertsScopeSelected": "Personnes spécifiques",
    "SalonInfo.staffBookingAlertsScopeHint": "Les propriétaires sont prévenus de toutes les réservations. Les employés ne sont prévenus que des réservations qui les concernent.",
    "SalonInfo.staffBookingAlertsNoStaff": "Aucun membre de l'équipe n'a encore d'adresse courriel.",
    "SalonInfo.staffBookingAlertsRoleOwner": "Propriétaire",
    "SalonInfo.staffBookingAlertsRoleEmployee": "Employé",
    "SalonInfo.salonSettingsUpdated": "Paramètres du salon mis à jour avec succès.",
    "SalonInfo.recurringAppointments": "Séries prises en ligne par vos clients",
    "SalonInfo.recurringAppointmentsHelp": "Laisse un client réserver un rendez-vous qui se répète. Votre équipe peut toujours créer des séries depuis le calendrier.",
    "SalonInfo.recurringEnabled": "Les clients peuvent réserver des rendez-vous récurrents",
    "SalonInfo.recurringDisabled": "Les rendez-vous récurrents sont désactivés",
    "SalonInfo.maxRecurringDuration": "Durée maximale de récurrence",
    "SalonInfo.staffRecurringDuration": "Durée maximale de récurrence (équipe)",
    "SalonInfo.staffRecurringDurationDescription": "Fenêtre maximale pour les séries récurrentes créées depuis le tableau de bord par vous et votre équipe",
    "SalonInfo.maxBlockedDuration": "Durée maximale pour les plages bloquées et ouvertures exceptionnelles",
    "SalonInfo.maxDuration3m": "3 mois",
    "SalonInfo.maxDuration6m": "6 mois",
    "SalonInfo.maxDuration9m": "9 mois",
    "SalonInfo.maxDuration12m": "1 an",
    "SalonInfo.maxDuration18m": "1 an et demi",
    "SalonInfo.maxDuration24m": "2 ans",
    "SalonInfo.maxDuration36m": "3 ans",
    "SalonInfo.maxDuration60m": "5 ans",
    "SalonInfo.autoApprove": "Approbation automatique des rendez-vous",
    "SalonInfo.autoApproveLockedByClientConfirmation": "Indisponible tant que les clients confirment eux-mêmes leur rendez-vous. Désactivez d'abord ce réglage.",
    "SalonInfo.autoApproveEnabled": "Les rendez-vous en attente sont approuvés automatiquement lorsqu'ils approchent de l'heure prévue",
    "SalonInfo.autoApproveDisabled": "L'approbation automatique est désactivée : tous les rendez-vous nécessitent une approbation manuelle",
    "SalonInfo.autoMarkPresent": "Marquer automatiquement les rendez-vous passés comme Présents",
    "SalonInfo.autoMarkPresentEnabled": "Les rendez-vous confirmés sont marqués Présents automatiquement une fois terminés",
    "SalonInfo.autoMarkPresentDisabled": "Les rendez-vous passés restent sans statut de présence tant que vous ne les marquez pas manuellement",
    "SalonInfo.autoMarkPresentDescription": "Filet de sécurité pour vos statistiques de fiabilité client : si personne ne marque un client Présent après son rendez-vous, le système le fait à votre place. Désactivez-le pour toujours marquer la présence manuellement. Vous pouvez toujours basculer n'importe quel rendez-vous en Absence à tout moment.",
    "SalonInfo.autoHideCancelled": "Retirer les rendez-vous annulés en un clic",
    "SalonInfo.autoHideCancelledEnabled": "Activé : annuler retire aussi du calendrier",
    "SalonInfo.autoHideCancelledDisabled": "Désactivé : les rendez-vous annulés restent en rouge jusqu'au retrait",
    "SalonInfo.autoHideCancelledDescription": "Une fois activé, annuler un rendez-vous le retire du calendrier en une étape au lieu de deux. Rien n'est supprimé : il reste dans le Journal des annulations, les rapports et l'historique client, et vous pouvez le réafficher depuis le Journal des annulations.",
    "SalonInfo.calendarChangeConfirmation": "Confirmer les modifications au calendrier",
    "SalonInfo.calendarChangeConfirmationEnabled": "Une fenêtre demande de confirmer chaque changement",
    "SalonInfo.calendarChangeConfirmationDisabled": "Les changements s'appliquent directement",
    "SalonInfo.calendarChangeConfirmationDescription": "S'applique quand vous déplacez ou étirez un rendez-vous ou une plage bloquée, à l'ordinateur comme au téléphone.",
    "SalonInfo.appointmentOverlap": "Autoriser les rendez-vous qui se chevauchent",
    "SalonInfo.overlapEnabled": "Les propriétaires et employés peuvent réserver des rendez-vous qui se chevauchent",
    "SalonInfo.overlapDisabled": "Les rendez-vous qui se chevauchent sont bloqués",
    "SalonInfo.appointmentOverlapDescription": "Utile quand un employé peut servir deux clients en parallèle pendant un temps de pause",
    "SalonInfo.outOfHoursBooking": "Rendez-vous hors horaire",
    "SalonInfo.outOfHoursBookingDescription": "Permet à votre équipe de placer un rendez-vous avant ou après l'horaire d'un employé, depuis le calendrier seulement, dans la plage horaire du calendrier. La réservation en ligne n'est pas touchée.",
    "SalonInfo.outOfHoursEnabled": "Les propriétaires et employés peuvent réserver hors de l'horaire d'un employé, dans le calendrier",
    "SalonInfo.outOfHoursDisabled": "Les rendez-vous restent dans l'horaire de chaque employé",
    "SalonInfo.partialRecurringSeries": "Créer les séries récurrentes malgré les dates impossibles",
    "SalonInfo.partialRecurringSeriesDescription": "Sans ce réglage, une seule date impossible refuse toute la série et rien n'est créé. Avec, les dates prises par un autre rendez-vous ou par un créneau bloqué sont laissées de côté, les autres sont réservées, et un message énumère ce qui a été sauté. S'applique aux séries prises depuis le tableau de bord.",
    "SalonInfo.partialRecurringSeriesEnabled": "Les dates impossibles sont sautées, le reste de la série est créé",
    "SalonInfo.partialRecurringSeriesDisabled": "Une seule date impossible refuse toute la série",
    "SalonInfo.maxBookingWindow": "Fenêtre de réservation maximale",
    "SalonInfo.maxBookingWindowDescription": "Jusqu'à quand les clients peuvent réserver en ligne",
    "SalonInfo.maxBookingUnlimited": "Illimitée",
    "SalonInfo.maxBooking1Week": "1 semaine",
    "SalonInfo.maxBooking2Weeks": "2 semaines",
    "SalonInfo.maxBooking1Month": "1 mois",
    "SalonInfo.maxBooking2Months": "2 mois",
    "SalonInfo.maxBooking3Months": "3 mois",
    "SalonInfo.maxBooking6Months": "6 mois",
    "SalonInfo.maxBooking1Year": "1 an",
    "SalonInfo.bookingSlotInterval": "Intervalle des plages horaires",
    "SalonInfo.bookingSlotIntervalDescription": "Intervalle de temps entre les créneaux de réservation affichés aux clients",
    "SalonInfo.payrollFrequency": "Fréquence de paie",
    "SalonInfo.payrollFrequencyDescription": "À quelle fréquence vous payez votre personnel. Détermine les périodes de paie, les heures supplémentaires et les salaires fixes.",
    "SalonInfo.payFreqWeekly": "Hebdomadaire",
    "SalonInfo.payFreqBiweekly": "Aux 2 semaines",
    "SalonInfo.payFreqMonthly": "Mensuelle",
    "SalonInfo.payrollAnchor": "Début du prochain cycle",
    "SalonInfo.payrollAnchorDescription": "Le premier jour de votre prochain cycle de paie. Il fixe aussi le jour où commence votre semaine de travail, celle qui sert à compter les heures supplémentaires.",
    "SalonInfo.payrollEnterpriseOnly": "Disponible avec le forfait Entreprise",
    "SalonInfo.payrollFrequencyLocked": "Verrouillé : votre première paie a été finalisée. Changer la fréquence maintenant chevaucherait des périodes de paie existantes.",
    "SalonInfo.slotInterval15": "Toutes les 15 minutes",
    "SalonInfo.slotInterval30": "Toutes les 30 minutes",
    "SalonInfo.slotInterval10": "Toutes les 10 minutes",
    "SalonInfo.calendarSlotInterval": "Intervalle du calendrier",
    "SalonInfo.calendarSlotIntervalHelp": "Taille des cases du calendrier et pas des heures proposées au personnel.",
    "SalonInfo.intervalSavedBoth": "Intervalle enregistré. La réservation en ligne suit aussi : toutes les {minutes} minutes.",
    "SalonInfo.intervalAlignTitle": "Aligner aussi la réservation en ligne ?",
    "SalonInfo.intervalAlignBody": "La réservation en ligne est réglée à toutes les {online} minutes. Voulez-vous aussi proposer des heures toutes les {next} minutes à vos clients ?",
    "SalonInfo.intervalAlignBoth": "Aligner les deux",
    "SalonInfo.intervalKeepOnline": "Garder {online} min en ligne",
    "SalonInfo.intervalDivergeTitle": "Êtes-vous sûr ?",
    "SalonInfo.intervalDivergeBody": "Vos clients verront des heures toutes les {next} minutes, alors que votre calendrier est découpé toutes les {calendar} minutes. Certains rendez-vous tomberont entre deux lignes du calendrier.",
    "SalonInfo.postCreateSilence": "Période de silence après la prise",
    "SalonInfo.postCreateSilenceDescription": "Seul un changement de date ou d'heure avise le client : les changements d'employé, de salle, de durée ou de services restent silencieux. Quand l'heure change, l'avis n'est pas envoyé tout de suite : les modifications faites pendant cette fenêtre sont regroupées en un seul message indiquant l'heure finale (et si vous revenez à l'heure d'origine, rien n'est envoyé). Les annulations avisent toujours immédiatement.",
    "SalonInfo.postCreateSilenceEnabled": "Les changements d'heure sont regroupés avant l'avis",
    "SalonInfo.postCreateSilenceDisabled": "Les changements d'heure avisent le client immédiatement",
    "SalonInfo.postCreateSilenceDuration": "Durée de la fenêtre",
    "SalonInfo.postCreateSilence1min": "1 minute",
    "SalonInfo.postCreateSilence5min": "5 minutes",
    "SalonInfo.postCreateSilence15min": "15 minutes",
    "SalonInfo.postCreateSilence30min": "30 minutes",
    "SalonInfo.postCreateSilence60min": "1 heure",
    "SalonInfo.autoApproveSameDay": "Seuil pour réservation le jour même",
    "SalonInfo.autoApproveAdvance": "Seuil pour réservation à l'avance",
    "SalonInfo.autoApproveImmediate": "Immédiatement (toujours)",
    "SalonInfo.autoApprove15min": "15 minutes avant",
    "SalonInfo.autoApprove30min": "30 minutes avant",
    "SalonInfo.autoApprove45min": "45 minutes avant",
    "SalonInfo.autoApprove60min": "1 heure avant",
    "SalonInfo.autoApprove12h": "12 heures avant",
    "SalonInfo.autoApprove24h": "24 heures avant",
    "SalonInfo.autoApprove48h": "48 heures avant",
    "SalonInfo.onlineBooking": "Réservation en ligne",
    "SalonInfo.onlineBookingEnabled": "Les clients peuvent réserver en ligne",
    "SalonInfo.onlineBookingDisabled": "La réservation en ligne est désactivée",
    "SalonInfo.hidePricesOnline": "Masquer les prix",
    "SalonInfo.hidePricesOnlineDescription": "Les prix ne sont pas montrés aux clients, ni sur la page de réservation ni dans leurs courriels de rendez-vous. Ils restent sur les factures.",
    "SalonInfo.hidePricesOnlineEnabled": "Les prix sont masqués lors de la réservation en ligne",
    "SalonInfo.hidePricesOnlineDisabled": "Les prix sont affichés lors de la réservation en ligne",
    "SalonInfo.showNextAvailability": "Afficher la prochaine disponibilité par employé",
    "SalonInfo.showNextAvailabilityDescription": "Les clients voient la prochaine disponibilité de chaque employé à l'étape du choix d'employé, pour choisir qui peut les prendre le plus tôt.",
    "SalonInfo.showNextAvailabilityEnabled": "La prochaine disponibilité est affichée lors de la réservation en ligne",
    "SalonInfo.showNextAvailabilityDisabled": "La prochaine disponibilité est masquée lors de la réservation en ligne",
    "SalonInfo.technicalSheetsEditable": "Fiches techniques modifiables",
    "SalonInfo.technicalSheetsEditableDescription": "Permet à votre équipe de corriger le texte d'une fiche technique déjà enregistrée. La date et l'employé restent figés, et chaque correction laisse une mention visible sur la fiche. Ce réglage vaut pour toutes vos succursales.",
    "SalonInfo.technicalSheetsEditableEnabled": "Modifiables",
    "SalonInfo.technicalSheetsEditableDisabled": "Verrouillées",
    "SalonInfo.employeeOnlineBooking": "Réservation par employé",
    "SalonInfo.employeeOnlineBookingDescription": "Contrôlez quels employés sont disponibles pour la réservation en ligne.",
    "SalonInfo.calendarColorMode": "Couleurs du calendrier",
    "SalonInfo.calendarDisplayHours": "Plage horaire du calendrier",
    "SalonInfo.calendarDisplayHoursHelp": "Heures visibles sur la grille du calendrier (format 24h). Par défaut 8 à 24.",
    "SalonInfo.hideNonWorking": "Masquer les employés qui ne travaillent pas (vue Jour)",
    "SalonInfo.hideNonWorkingDescription": "En vue Jour, n'afficher que les colonnes des employés prévus ce jour-là (plus ceux ayant un rendez-vous ou un blocage personnel).",
    "SalonInfo.hideNonWorkingNeedsIndividual": "Disponible uniquement avec les horaires individuels.",
    "SalonInfo.columnDensity": "Largeur des colonnes d'employés",
    "SalonInfo.collapsiblePickers": "Listes repliées",
    "SalonInfo.collapsiblePickersHelp": "Les listes de services et de produits du rendez-vous et de la facture s'ouvrent repliées par catégorie.",
    "SalonInfo.collapsiblePickersEnabled": "Activées : dans la prise de rendez-vous et la facture, les services et les produits sont rangés dans des groupes repliés, un clic montre leur contenu.",
    "SalonInfo.collapsiblePickersDisabled": "Désactivées : les listes de services et de produits affichent tout d'un coup.",
    "SalonInfo.allowNegativeStock": "Permettre le stock négatif",
    "SalonInfo.allowNegativeStockDesc": "Permet de vendre un produit dont le stock est à zéro. Une confirmation est demandée à la caisse, et le stock passe sous zéro jusqu'au prochain réapprovisionnement. Les transferts et les ajustements d'inventaire restent bloqués sous zéro.",
    "SalonInfo.greencircleFeeLabel": "Facturer des frais Green Circle",
    "SalonInfo.greencircleFeeDesc": "Un champ « Frais Green Circle » apparaît sur chaque facture. Vous y saisissez le montant à facturer.",
    "SalonInfo.greencircleFeeTaxableLabel": "Ces frais sont taxables",
    "SalonInfo.greencircleFeeTaxableDesc": "Taxables, les frais s'ajoutent avant les taxes. Sinon, ils s'ajoutent après les taxes, comme un pourboire.",
    "SalonInfo.greencircleFeeSaveError": "Le réglage n'a pas pu être enregistré.",
    "SalonInfo.columnDensityDescription": "Largeur des colonnes d'employés en vue Jour sur ordinateur. Le texte des cartes de rendez-vous reste identique à tous les réglages.",
    "SalonInfo.columnDensity.wide": "Large",
    "SalonInfo.columnDensity.medium": "Moyen",
    "SalonInfo.columnDensity.narrow": "Étroit",
    "SalonInfo.columnDensityHint.wide": "L'affichage d'origine, et le plus lisible. Environ 8 colonnes sur un écran courant.",
    "SalonInfo.columnDensityHint.medium": "Des colonnes plus étroites, sans rien changer aux en-têtes ni aux cartes. Environ 12 à 15 colonnes.",
    "SalonInfo.columnDensityHint.narrow": "Le maximum de colonnes. Seul l'en-tête cède du terrain : avatar plus petit, nom plus court, heures abrégées. Les cartes de rendez-vous restent identiques.",
    "SalonInfo.hideNonWorkingEnabled": "Les employés qui ne travaillent pas sont masqués",
    "SalonInfo.hideNonWorkingDisabled": "Tous les employés sont affichés",
    "SalonInfo.calendarDisplayStart": "Heure de début",
    "SalonInfo.calendarDisplayEnd": "Heure de fin",
    "SalonInfo.displayHoursInvalid": "L'heure de début doit être inférieure à l'heure de fin (0 à 23 / 1 à 24).",
    "SalonInfo.colorByEmployee": "Par employé",
    "SalonInfo.colorByService": "Par service",
    "SalonInfo.colorByStatus": "Par statut",
    "SalonInfo.greyOutPastAppointments": "Griser les rendez-vous terminés",
    "SalonInfo.greyOutPastAppointmentsEnabled": "Les rendez-vous passés deviennent gris ardoise une fois terminés",
    "SalonInfo.greyOutPastAppointmentsDisabled": "Les rendez-vous passés gardent leur couleur normale",
    "SalonInfo.greyOutPastAppointmentsDescription": "Repère visuel sur le calendrier : dès que l'heure de fin d'un rendez-vous est passée, il devient gris ardoise atténué pour distinguer d'un coup d'œil ce qui est terminé de ce qui reste à venir. Les rendez-vous annulés (rouge) et les absences (violet) gardent leur couleur.",
    "SalonInfo.darkMode": "Mode sombre",
    "SalonInfo.darkModeEnabled": "Couleurs sombres",
    "SalonInfo.darkModeDisabled": "Couleurs claires",
    "SalonInfo.darkModeDescription": "Ne vaut que pour cet appareil, et reste éteint tant que vous ne l'allumez pas. Chaque personne garde son propre choix, et rien ne change pour vos clientes.",
    "SalonInfo.salonLogo": "Logo du salon",
    "SalonInfo.salonLogoDescription": "Téléversez le logo de votre salon. Il sera affiché sur votre page de réservation en ligne.",
    "SalonInfo.removeLogo": "Retirer le logo",
    "SalonInfo.uploadLogo": "Téléverser le logo",
    "SalonInfo.uploadingLogo": "Téléversement...",
    "SalonInfo.logoUpdated": "Logo du salon mis à jour avec succès.",
    "SalonInfo.logoRemoved": "Logo du salon supprimé.",
    "SalonInfo.invalidLogoFile": "Veuillez sélectionner un fichier image.",
    "SalonInfo.logoFileTooLarge": "Le fichier du logo doit faire moins de 5 Mo.",
    "SalonInfo.salonPolicies": "Politiques du salon",
    "SalonInfo.salonPoliciesDescription": "Définissez les politiques de votre salon qui seront visibles par vos clients lors de la réservation.",
    "SalonInfo.sectionCancellation": "Annulation",
    "SalonInfo.cancellationPolicy": "Politique d'annulation",
    "SalonInfo.cancellationPolicyPlaceholder": "Décrivez votre politique d'annulation (ex: toute annulation doit être faite au moins 24 heures à l'avance...)",
    "SalonInfo.cancelCutoff.toggleLabel": "Bloquer l'annulation et le report côté client",
    "SalonInfo.cancelCutoff.toggleEnabled": "Activé",
    "SalonInfo.cancelCutoff.toggleDisabled": "Désactivé",
    "SalonInfo.cancelCutoff.delayLabel": "Délai avant le rendez-vous",
    "SalonInfo.cancelCutoff.option6": "6 heures",
    "SalonInfo.cancelCutoff.option12": "12 heures",
    "SalonInfo.cancelCutoff.option18": "18 heures",
    "SalonInfo.cancelCutoff.option24": "24 heures",
    "SalonInfo.cancelCutoff.option48": "48 heures",
    "SalonInfo.cancelCutoff.option72": "72 heures",
    "SalonInfo.cancelCutoff.helpText": "Vos clients ne pourront pas annuler ni reporter leur rendez-vous via l'app moins de {hours} h avant l'heure prévue. Vous pouvez toujours le faire pour eux depuis le tableau de bord.",
    "SalonInfo.sectionLateness": "Retard",
    "SalonInfo.tabProfile": "Profil",
    "SalonInfo.tabIdentity": "Identité",
    "SalonInfo.tabPolicies": "Politiques",
    "SalonInfo.tabGeneral": "Paramètres généraux",
    "SalonInfo.tabPayments": "Paiements",
    "SalonInfo.sectionOnlinePayments": "Paiements en ligne",
    "SalonInfo.tabLocations": "Emplacements",
    "SalonInfo.headerSubtitle": "Gérez les informations, les politiques et les réglages de votre salon.",

    // Onglet Paramètres généraux : sections (2026-10-13)
    "SalonInfo.general.share.title": "Partager votre page de réservation",
    "SalonInfo.general.share.intro": "Le lien et le code QR qui mènent vos clients directement à votre page.",
    "SalonInfo.general.online.title": "Réservation en ligne",
    "SalonInfo.general.online.intro": "Ce que vos clients voient et peuvent faire quand ils réservent eux-mêmes. Rien ici ne touche le calendrier de votre équipe.",
    "SalonInfo.general.online.offNote": "La réservation en ligne est coupée : les réglages ci-dessous sont gardés, et reprendront dès que vous la rallumerez.",
    "SalonInfo.general.calendar.title": "Calendrier",
    "SalonInfo.general.calendar.intro": "Ce que votre équipe voit dans le calendrier, et comment il réagit quand elle prend, déplace ou annule un rendez-vous.",
    "SalonInfo.general.calendar.grid": "Grille",
    "SalonInfo.general.calendar.display": "Affichage du calendrier",
    "SalonInfo.general.calendar.teamActions": "Gestes de l'équipe",
    "SalonInfo.general.notifications.title": "Notifications",
    "SalonInfo.general.notifications.intro": "Les courriels et SMS envoyés à vos clients, et les alertes envoyées à votre équipe.",
    "SalonInfo.general.notifications.reminders": "Rappels aux clients",
    "SalonInfo.general.notifications.confirmation": "Confirmation et approbation",
    "SalonInfo.general.notifications.afterChange": "Après une modification",
    "SalonInfo.general.notifications.afterAppointment": "Après le rendez-vous",
    "SalonInfo.general.notifications.teamAlerts": "Alertes à votre équipe",
    "SalonInfo.general.notifications.reviewRequestsHelp": "Exige le lien Google, à saisir dans l'onglet Identité.",
    "SalonInfo.general.cash.title": "Caisse et factures",
    "SalonInfo.general.cash.intro": "Ce qui se passe à la caisse quand une facture est payée.",
    "SalonInfo.general.cash.paymentMethods": "Méthodes de paiement",
    "SalonInfo.general.cash.drawer": "Tiroir et impression",
    "SalonInfo.general.cash.invoice": "Facture",
    "SalonInfo.general.clients.title": "Clients",
    "SalonInfo.general.clients.intro": "Ce que votre équipe peut corriger dans les dossiers de vos clients.",
    "SalonInfo.general.region.title": "Région et langue",
    "SalonInfo.general.region.intro": "Les taxes, l'heure et la langue des messages envoyés à vos clients.",
    "SalonInfo.general.payroll.title": "Paie",
    "SalonInfo.general.payroll.intro": "Le rythme de la paie de votre équipe. Ces réglages ne touchent pas la réservation.",
    "SalonInfo.general.payroll.ownerOnly": "Seul le propriétaire peut modifier la paie.",
    "SalonInfo.general.device.title": "Sur cet appareil",
    "SalonInfo.general.device.intro": "Ce réglage ne vaut que pour cet ordinateur ou ce téléphone, pas pour votre équipe.",
    "SalonInfo.tileOnlineBooking": "Réservation en ligne",
    "SalonInfo.tileBookingWindow": "Fenêtre de résa.",
    "SalonInfo.tileTimezone": "Fuseau horaire",
    "SalonInfo.tileLanguage": "Langue",
    "SalonInfo.tileEnabled": "Activée",
    "SalonInfo.tileDisabled": "Désactivée",
    "SalonInfo.tileUnlimited": "Illimité",
    "SalonInfo.tileDaysSuffix": "jours",
    "SalonInfo.subAvailability": "Disponibilité",
    "SalonInfo.subRecurrence": "Récurrence",
    "SalonInfo.subAutoApprove": "Approbation automatique",
    "SalonInfo.subCalendarDisplay": "Affichage du calendrier",
    "SalonInfo.subRemindersBlocks": "Rappels et blocages",
    "SalonInfo.locationsDivider": "Emplacements multiples",
    "SalonInfo.identityCard": "Identité du salon",
    "SalonInfo.identityCardDescription": "Nom, logo et coordonnées affichés aux clients",
    "SalonInfo.taxNumbers": "Numéros de taxe",
    "SalonInfo.taxNumbersDescription": "Apparaissent sur vos factures (Québec)",
    "SalonInfo.linkAndQR": "Lien direct & QR code",
    "SalonInfo.linkAndQRDescription": "Partagez ce lien à vos clients",
    "SalonInfo.activationCard": "Activation de la réservation en ligne",
    "SalonInfo.activationCardDescription": "Activez le service et choisissez les employés disponibles",
    "SalonInfo.bookingRules": "Règles de réservation",
    "SalonInfo.bookingRulesDescription": "Disponibilités, intervalles, récurrence, approbation",
    "SalonInfo.colorByEmployeeShort": "Par employé",
    "SalonInfo.colorByServiceShort": "Par service",
    "SalonInfo.colorByRoomShort": "Par salle",
    "SalonInfo.colorByRoomDescription": "Salon principal, salle B...",
    "SalonInfo.colorModeHelp": "Comment les rendez-vous sont colorés dans la vue agenda",
    "SalonInfo.latePolicy": "Politique de retard",
    "SalonInfo.latePolicyPlaceholder": "Décrivez votre politique de retard (ex: un retard de plus de 15 minutes sans avis entraînera l'annulation automatique...)",
    "SalonInfo.savePolicies": "Enregistrer les politiques",
    "SalonInfo.policiesUpdated": "Politiques du salon mises à jour avec succès.",
    "SalonInfo.viewPolicies": "Voir les politiques",
    "SalonInfo.website": "Site web",
    "SalonInfo.policiesDialogTitle": "Politiques du salon",
    "SalonInfo.understood": "Compris",
    "SalonInfo.timezoneLabel": "Fuseau horaire du salon",
    "SalonInfo.defaultLanguageLabel": "Langue par défaut",
    "SalonInfo.provinceLabel": "Province",
    "SalonInfo.province.confirmTitle": "Changer de province ?",
    "SalonInfo.province.confirmBody": "Cela met à jour vos taxes de facturation et peut changer votre fuseau horaire. Vos factures déjà émises ne sont pas modifiées.",
    "SalonInfo.province.syncTimezone": "Mettre aussi mon fuseau à {tz}",
    "SalonInfo.province.taxesPreview": "Taxes : {lines}",
    "SalonInfo.province.nudge": "Confirmez la province de votre salon pour que vos taxes et votre fuseau soient corrects.",
    "SalonInfo.province.nudgeCta": "Choisir la province",
    "SalonInfo.tz.pacific": "Heure du Pacifique (HNP, UTC-8)",
    "SalonInfo.tz.mountain": "Heure des Rocheuses (HNR, UTC-7)",
    "SalonInfo.tz.central": "Heure du Centre (HNC, UTC-6)",
    "SalonInfo.tz.eastern": "Heure de l'Est (HNE, UTC-5)",
    "SalonInfo.tz.atlantic": "Heure de l'Atlantique (HNA, UTC-4)",
    "SalonInfo.tz.newfoundland": "Heure de Terre-Neuve (HNT, UTC-3:30)",

    // Location Manager
    "LocationManager.title": "Gestion multi-emplacements",
    "LocationManager.description": "Gérez vos emplacements de salon et paramètres",
    "LocationManager.addLocation": "Ajouter un emplacement",
    "LocationManager.manageFromDefault": "Les emplacements se gèrent depuis le salon par défaut.",
    "LocationManager.activeLocations": "Emplacements actifs",
    "LocationManager.archived": "Archivé",
    "LocationManager.onlineBookingAlways": "Réservation en ligne (Toujours activée)",
    "LocationManager.publicListingAlways": "Liste publique (Toujours activée)",
    "LocationManager.onlineBooking": "Réservation en ligne",
    "LocationManager.publicListing": "Liste publique",
    "LocationManager.noLocations": "Aucun emplacement supplémentaire pour le moment",
    "LocationManager.clickToAdd": "Cliquez sur « Ajouter un emplacement » pour commencer",
    "LocationManager.restore": "Restaurer",
    "LocationManager.deleteForever": "Supprimer définitivement",
    "LocationManager.noArchived": "Aucun emplacement archivé",
    "LocationManager.addNewLocation": "Ajouter un nouvel emplacement",
    "LocationManager.addLocationDescription": "Entrez les informations de votre nouvel emplacement de salon.",
    "LocationManager.locationName": "Nom de l'emplacement *",
    "LocationManager.locationNamePlaceholder": "Succursale centre-ville",
    "LocationManager.address": "Adresse",
    "LocationManager.addressPlaceholder": "123 Rue Principale, Ville, Province",
    "LocationManager.phone": "Téléphone",
    "LocationManager.phonePlaceholder": "+1 (555) 000-0000",
    "LocationManager.email": "Courriel",
    "LocationManager.emailPlaceholder": "emplacement@salon.com",
    "LocationManager.cancel": "Annuler",
    "LocationManager.archiveTitle": "Archiver l'emplacement ?",
    "LocationManager.archiveDescription":
      "Cela archivera l'emplacement. Vous pourrez le restaurer plus tard à partir de l'onglet Archivé.",
    "LocationManager.archive": "Archiver",
    "LocationManager.permanentDeleteTitle": "Supprimer définitivement ?",
    "LocationManager.permanentDeleteDescription":
      "Cette action ne peut pas être annulée. Toutes les données associées à cet emplacement seront définitivement supprimées.",
    "LocationManager.permanentDelete": "Supprimer définitivement",
    "LocationManager.success": "Succès",
    "LocationManager.error": "Erreur",
    "LocationManager.locationAdded": "Emplacement ajouté avec succès",
    "LocationManager.locationArchived": "Emplacement archivé avec succès",
    "LocationManager.locationRestored": "Emplacement restauré avec succès",
    "LocationManager.locationDeleted": "Emplacement supprimé définitivement",
    "LocationManager.nameRequired": "Le nom de l'emplacement est obligatoire",
    "LocationManager.loading": "Chargement des emplacements...",
    "LocationManager.calendarColorMode": "Couleurs du calendrier",
    "LocationManager.colorByEmployee": "Par employé",
    "LocationManager.colorByService": "Par service",
    "LocationManager.limitReached": "Limite atteinte. Contactez-nous pour ajouter des emplacements.",
    "LocationManager.upgradeForMultiLocation": "Passez au plan Enterprise pour accéder à la gestion multi-emplacements",
    "LocationManager.employees": "Employés",
    "LocationManager.services": "Services",
    "LocationManager.apptsThisMonth": "RDV ce mois",
    "LocationManager.revenueThisMonth": "Revenus ce mois",
    "LocationManager.online": "En ligne",
    "LocationManager.offline": "Hors ligne",
    "LocationManager.defaultIndicator": "Salon par défaut",
    "LocationManager.actionsMenu": "Actions du salon",

    // Buffer Time
    "BufferTime.title": "Temps de pause entre rendez-vous",
    "BufferTime.description":
      "Ce temps est automatiquement ajouté après chaque rendez-vous pour le nettoyage ou la préparation. S'applique uniquement aux réservations client.",
    "BufferTime.noBuffer": "Aucun",
    "BufferTime.updated": "Temps de pause mis à jour",
    "BufferTime.noHairdressers": "Ajoutez des employés d'abord pour configurer les temps de pause.",

    // Attribution sans préférence
    "Owner.salonInfo.anyAssignment.title": "Attribution sans préférence",
    "Owner.salonInfo.anyAssignment.help":
      "Quand une cliente réserve en ligne sans choisir de professionnel, voici comment le rendez-vous lui est attribué.",
    "Owner.salonInfo.anyAssignment.random": "Au hasard",
    "Owner.salonInfo.anyAssignment.randomHelp":
      "Tirage parmi les professionnels libres à cette heure. C'est le réglage actuel.",
    "Owner.salonInfo.anyAssignment.leastLoaded": "Le moins chargé ce jour-là",
    "Owner.salonInfo.anyAssignment.leastLoadedHelp":
      "Celui qui a le moins de minutes déjà réservées dans sa journée.",
    "Owner.salonInfo.anyAssignment.roundRobin": "Chacun son tour",
    "Owner.salonInfo.anyAssignment.roundRobinHelp":
      "Celui qui a attendu le plus longtemps depuis sa dernière attribution sans préférence.",
    "Owner.salonInfo.anyAssignment.priorityOrder": "Ordre de priorité",
    "Owner.salonInfo.anyAssignment.priorityOrderHelp":
      "Le premier professionnel libre dans l'ordre que vous définissez ci-dessous.",
    "Owner.salonInfo.anyAssignment.priorityListHelp":
      "Glissez les professionnels pour changer leur ordre. Une nouvelle personne arrive en fin de liste.",
    "Owner.salonInfo.anyLocation.title": "Réservation dans n'importe quel emplacement",
    "Owner.salonInfo.anyLocation.help":
      "Vos clients peuvent choisir N'importe quel emplacement : le système leur attribue le premier emplacement libre, dans l'ordre ci-dessous.",
    "Owner.salonInfo.anyLocation.brandWide": "Ce réglage vaut pour tous vos emplacements.",
    "Owner.salonInfo.anyLocation.toggle": "Proposer N'importe quel emplacement",
    "Owner.salonInfo.anyLocation.toggleHelp":
      "Les clients verront cette option seulement si au moins un service est offert dans deux emplacements.",
    "Owner.salonInfo.anyLocation.priorityTitle": "Ordre de priorité des emplacements",
    "Owner.salonInfo.anyLocation.priorityHelp":
      "Glissez les emplacements pour changer leur ordre. À heure égale, le premier libre est retenu.",
    "Owner.salonInfo.anyLocation.bookingOff": "Réservation en ligne désactivée",
    "Owner.salonInfo.anyLocation.tableTitle": "Services équivalents",
    "Owner.salonInfo.anyLocation.tableHelp":
      "Indiquez, ligne par ligne, quel service de chaque emplacement correspond au même service. Seuls les services liés sont proposés en mode N'importe quel emplacement.",
    "Owner.salonInfo.anyLocation.notOffered": "Non offert ici",
    "Owner.salonInfo.anyLocation.hiddenOnline": "(masqué en ligne)",
    "Owner.salonInfo.anyLocation.notBookableOnline": "(non réservable en ligne)",
    "Owner.salonInfo.anyLocation.minutes": "min",
    "Owner.salonInfo.anyLocation.suggested": "Suggérée",
    "Owner.salonInfo.anyLocation.singleServiceNote": "Un service seul n'est pas proposé aux clients.",
    "Owner.salonInfo.anyLocation.addRow": "Ajouter une ligne",
    "Owner.salonInfo.anyLocation.removeRow": "Retirer",
    "Owner.salonInfo.anyLocation.suggest": "Suggérer des correspondances",
    "Owner.salonInfo.anyLocation.noSuggestion": "Aucune correspondance évidente trouvée.",
    "Owner.salonInfo.anyLocation.save": "Enregistrer",
    "Owner.salonInfo.anyLocation.saving": "Enregistrement…",
    "Owner.salonInfo.anyLocation.loadError": "Impossible de charger ce réglage.",
    "Owner.salonInfo.anyLocation.retry": "Réessayer",
    "Owner.salonInfo.anyLocation.dragHandle": "Déplacer",
    "Owner.salonInfo.anyLocation.errors.notRootOwner": "Seul le propriétaire principal peut modifier ce réglage.",
    "Owner.salonInfo.anyLocation.errors.salonNotFound": "Salon introuvable.",
    "Owner.salonInfo.anyLocation.errors.groupEmpty": "Choisissez au moins un service sur cette ligne.",
    "Owner.salonInfo.anyLocation.errors.serviceOutsideBrand":
      "Ce service n'appartient plus à vos emplacements actifs.",
    "Owner.salonInfo.anyLocation.errors.duplicateLocation":
      "Une ligne ne peut contenir qu'un service par emplacement.",
    "Owner.salonInfo.anyLocation.errors.serviceInTwoGroups": "Ce service est déjà utilisé sur une autre ligne.",
    "Owner.salonInfo.anyLocation.errors.malformed":
      "Les correspondances n'ont pas pu être lues. Rechargez la page.",
    "Owner.salonInfo.anyLocation.errors.foreignLocation": "Un emplacement de la liste n'appartient pas à votre marque.",
  },
};
